import {
    createAsyncThunk,
    createEntityAdapter,
    createSlice
} from '@reduxjs/toolkit';
import { useSelector } from 'react-redux';

import * as api from '../services/api';
import { RootState } from '../store';
import { HistoryElement } from '../types/history';

const historyAdapter = createEntityAdapter();

interface HistoryState {
    historyLoading: boolean;
    historyError: string | null;
    completedWork: HistoryElement[] | null;
}

const initialState = historyAdapter.getInitialState({
    historyLoading: false,
    historyError: null,
    completedWork: null
} as HistoryState);

export const loadWorkHistory = createAsyncThunk(
    'history/loadWorkHistory',
    async (_options: Record<string, never>, { rejectWithValue }) => {
        try {
            return await api.fetchWorkHistory();
        } catch (err) {
            return rejectWithValue(err);
        }
    }
);

export const markBlameIssuesRead = createAsyncThunk(
    'history/markBlameIssuesRead',
    async (blameIssuesId: number, { rejectWithValue }) => {
        try {
            await api.updateQuestBlameIssue(blameIssuesId);
            return blameIssuesId;
        } catch (err) {
            return rejectWithValue(err);
        }
    }
);

const historySlice = createSlice({
    name: 'history',
    initialState,
    reducers: {
        clearWorkHistoryError(state) {
            state.historyError = null;
        }
    },
    extraReducers: (builder) => {
        builder.addCase(loadWorkHistory.pending, (state) => {
            state.historyLoading = true;
            state.completedWork = null;
            state.historyError = null;
        });
        builder.addCase(loadWorkHistory.fulfilled, (state, action) => {
            state.historyLoading = false;
            state.completedWork = action.payload;
        });
        builder.addCase(loadWorkHistory.rejected, (state, action: any) => {
            state.historyLoading = false;
            state.historyError = action.payload.error;
        });
        builder.addCase(markBlameIssuesRead.fulfilled, (state, action) => {
            if (state.completedWork) {
                // Find and update the specific record
                state.completedWork.forEach(work => {
                    if (work.questBlameIssues?.blameId === action.payload) {
                        work.questBlameIssues.blameRead = true;
                    }
                });
            }
        });
    }
});

export const { clearWorkHistoryError } = historySlice.actions;

export default historySlice.reducer;

interface HistorySelectorsType {
    historyLoading: boolean;
    historyError: string | null;
    completedWork: HistoryElement[] | null;
    unreadBlameIssuesCount: number;
}

export const HistorySelectors = (): HistorySelectorsType => {

    const historyLoading = useSelector(
        (state: RootState) => state.history.historyLoading
    );

    const historyError = useSelector(
        (state: RootState) => state.history.historyError
    );

    const completedWork = useSelector(
        (state: RootState) => state.history.completedWork
    );

    const unreadBlameIssuesCount = useSelector(
        (state: RootState) => {
            return state.history.completedWork?.filter(
                (w) => w.questBlameIssues && !w.questBlameIssues.blameRead
            ).length || 0;
        }
    );

    return {
        historyLoading,
        historyError,
        completedWork,
        unreadBlameIssuesCount
    };
};

